import * as React from "react"

import { cn } from "@/lib/utils"

// Enhanced table with mobile-responsive features
interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  responsive?: "scroll" | "stack" | "hide-columns" | "auto";
  touchOptimized?: boolean;
  showScrollIndicator?: boolean;
}

const Table = React.forwardRef<
  HTMLTableElement,
  TableProps
>(({ className, responsive = "auto", touchOptimized = true, showScrollIndicator = true, ...props }, ref) => {
  const [showLeftIndicator, setShowLeftIndicator] = React.useState(false);
  const [showRightIndicator, setShowRightIndicator] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Check scroll position for indicators
  const checkScrollPosition = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftIndicator(scrollLeft > 0);
    setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();
    container.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  const containerClasses = cn(
    "relative w-full",
    // Enhanced mobile scrolling
    "overflow-x-auto",
    // Touch-friendly scrolling on mobile
    touchOptimized && "2xs:overflow-x-scroll mobile-sm:overflow-x-scroll mobile-md:overflow-x-scroll",
    // Custom scrollbar styling
    "scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400",
    // Smooth scroll behavior
    "scroll-smooth",
    // Better touch scrolling on iOS
    "[&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:rounded-full",
    "[&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full",
    "[&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400"
  );

  return (
    <div className={containerClasses} ref={scrollContainerRef}>
      {/* Left scroll indicator */}
      {showScrollIndicator && showLeftIndicator && (
        <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none 2xs:block mobile-sm:block mobile-md:block md:hidden" />
      )}
      
      {/* Right scroll indicator */}
      {showScrollIndicator && showRightIndicator && (
        <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none 2xs:block mobile-sm:block mobile-md:block md:hidden" />
      )}
      
      <table
        ref={ref}
        className={cn(
          "w-full caption-bottom",
          // Responsive text sizing
          "text-xs 2xs:text-xs mobile-sm:text-sm mobile-md:text-sm md:text-sm",
          // Minimum width for horizontal scroll tables
          responsive === "scroll" && "min-w-full",
          // Touch-optimized spacing
          touchOptimized && "2xs:min-w-[640px] mobile-sm:min-w-[640px] mobile-md:min-w-[700px] mobile-lg:min-w-[800px] md:min-w-0",
          className
        )}
        {...props}
      />
    </div>
  );
})
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead 
    ref={ref} 
    className={cn(
      "[&_tr]:border-b",
      // Sticky headers on mobile for better UX
      "2xs:sticky 2xs:top-0 2xs:bg-white 2xs:z-10 mobile-sm:sticky mobile-sm:top-0 mobile-sm:bg-white mobile-sm:z-10",
      // Enhanced mobile styling
      "md:relative md:bg-transparent md:z-auto",
      className
    )} 
    {...props} 
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      "[&_tr:last-child]:border-0",
      // Better mobile row spacing
      "2xs:[&_tr]:border-b-2 mobile-sm:[&_tr]:border-b mobile-md:[&_tr]:border-b",
      className
    )}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      // Responsive footer styling
      "2xs:text-xs mobile-sm:text-sm md:text-sm",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  touchOptimized?: boolean;
}

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  TableRowProps
>(({ className, touchOptimized = true, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      // Touch-friendly row heights
      touchOptimized && "2xs:min-h-[56px] mobile-sm:min-h-[52px] mobile-md:min-h-[48px] md:min-h-[44px]",
      // Better mobile row styling
      "2xs:border-b-gray-200 mobile-sm:border-b-gray-200",
      // Improved touch targets
      touchOptimized && "2xs:hover:bg-gray-50 2xs:active:bg-gray-100",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  priority?: "high" | "medium" | "low";
  hideOnMobile?: boolean;
}

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  TableHeadProps
>(({ className, priority = "medium", hideOnMobile = false, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      // Base styles with responsive height
      "text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      "h-10 2xs:h-12 mobile-sm:h-12 mobile-md:h-12 md:h-12",
      // Responsive padding
      "px-2 2xs:px-2 mobile-sm:px-3 mobile-md:px-4 md:px-4",
      // Responsive text size
      "text-xs 2xs:text-xs mobile-sm:text-xs mobile-md:text-sm md:text-sm",
      // Column priority-based hiding
      priority === "low" && hideOnMobile && "hidden mobile-lg:table-cell",
      priority === "medium" && hideOnMobile && "hidden mobile-md:table-cell",
      // Sticky header shadow on scroll
      "border-b-2 border-gray-200",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  priority?: "high" | "medium" | "low";
  hideOnMobile?: boolean;
  touchOptimized?: boolean;
}

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  TableCellProps
>(({ className, priority = "medium", hideOnMobile = false, touchOptimized = true, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "align-middle [&:has([role=checkbox])]:pr-0",
      // Responsive padding for touch-friendly cells
      touchOptimized ? "p-2 2xs:p-2 mobile-sm:p-3 mobile-md:p-4 md:p-4" : "p-4",
      // Responsive text sizing
      "text-xs 2xs:text-xs mobile-sm:text-xs mobile-md:text-sm md:text-sm",
      // Column priority-based hiding
      priority === "low" && hideOnMobile && "hidden mobile-lg:table-cell",
      priority === "medium" && hideOnMobile && "hidden mobile-md:table-cell",
      // Better mobile line height
      "2xs:leading-tight mobile-sm:leading-normal",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn(
      "mt-4 text-muted-foreground",
      // Responsive text sizing
      "text-xs 2xs:text-xs mobile-sm:text-sm md:text-sm",
      className
    )}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

// Mobile Card-based Layout Components
interface TableCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  touchOptimized?: boolean;
}

const TableCard = React.forwardRef<
  HTMLDivElement,
  TableCardProps
>(({ className, title, subtitle, actions, touchOptimized = true, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card p-4 shadow-sm",
      // Touch-friendly spacing and sizing
      touchOptimized && "2xs:p-3 mobile-sm:p-4 mobile-md:p-4",
      // Better mobile interaction
      "2xs:active:bg-gray-50 mobile-sm:hover:shadow-md transition-shadow",
      className
    )}
    {...props}
  >
    {(title || subtitle || actions) && (
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={cn(
              "font-medium text-gray-900 truncate",
              "text-sm 2xs:text-sm mobile-sm:text-base"
            )}>
              {title}
            </h4>
          )}
          {subtitle && (
            <p className={cn(
              "text-muted-foreground truncate",
              "text-xs 2xs:text-xs mobile-sm:text-sm"
            )}>
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {actions}
          </div>
        )}
      </div>
    )}
    <div className="space-y-2">
      {children}
    </div>
  </div>
))
TableCard.displayName = "TableCard"

interface TableCardFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: React.ReactNode;
  priority?: "high" | "medium" | "low";
  hideOnMobile?: boolean;
}

const TableCardField = React.forwardRef<
  HTMLDivElement,
  TableCardFieldProps
>(({ className, label, value, priority = "medium", hideOnMobile = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex justify-between items-start gap-2",
      // Priority-based hiding
      priority === "low" && hideOnMobile && "hidden mobile-lg:flex",
      priority === "medium" && hideOnMobile && "hidden mobile-md:flex",
      className
    )}
    {...props}
  >
    <span className={cn(
      "text-muted-foreground font-medium shrink-0",
      "text-xs 2xs:text-xs mobile-sm:text-sm"
    )}>
      {label}:
    </span>
    <span className={cn(
      "text-gray-900 text-right break-words",
      "text-xs 2xs:text-xs mobile-sm:text-sm"
    )}>
      {value}
    </span>
  </div>
))
TableCardField.displayName = "TableCardField"

// Touch-friendly Action Button Component
interface TableActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  label?: string;
  variant?: "default" | "destructive" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
}

const TableActionButton = React.forwardRef<
  HTMLButtonElement,
  TableActionButtonProps
>(({ className, icon, label, variant = "ghost", size = "sm", children, ...props }, ref) => {
  const sizeClasses = {
    sm: "h-8 w-8 2xs:h-10 2xs:w-10 mobile-sm:h-9 mobile-sm:w-9",
    md: "h-10 w-10 2xs:h-11 2xs:w-11 mobile-sm:h-10 mobile-sm:w-10", 
    lg: "h-12 w-12 2xs:h-12 2xs:w-12 mobile-sm:h-11 mobile-sm:w-11"
  };

  const variantClasses = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground"
  };

  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        // Touch-friendly minimum size (44px)
        "min-h-[44px] min-w-[44px]",
        // Responsive sizing
        sizeClasses[size],
        variantClasses[variant],
        // Better touch interaction
        "2xs:active:scale-95 2xs:transition-transform",
        className
      )}
      {...props}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      {label && <span className="sr-only">{label}</span>}
      {children}
    </button>
  );
})
TableActionButton.displayName = "TableActionButton"

// Responsive Table Container that switches between table and cards
interface ResponsiveTableProps {
  data: any[];
  columns: {
    key: string;
    header: string;
    priority?: "high" | "medium" | "low";
    hideOnMobile?: boolean;
    render?: (item: any) => React.ReactNode;
  }[];
  cardTitle?: (item: any) => string;
  cardSubtitle?: (item: any) => string;
  actions?: (item: any) => React.ReactNode;
  breakpoint?: "mobile-sm" | "mobile-md" | "mobile-lg" | "md";
  className?: string;
}

const ResponsiveTable = ({ 
  data, 
  columns, 
  cardTitle, 
  cardSubtitle, 
  actions, 
  breakpoint = "mobile-md",
  className 
}: ResponsiveTableProps) => {
  const breakpointClasses = {
    "mobile-sm": "block mobile-sm:hidden",
    "mobile-md": "block mobile-md:hidden", 
    "mobile-lg": "block mobile-lg:hidden",
    "md": "block md:hidden"
  };

  const tableClasses = {
    "mobile-sm": "hidden mobile-sm:block",
    "mobile-md": "hidden mobile-md:block",
    "mobile-lg": "hidden mobile-lg:block", 
    "md": "hidden md:block"
  };

  return (
    <div className={className}>
      {/* Mobile Card Layout */}
      <div className={breakpointClasses[breakpoint]}>
        <div className="space-y-3 2xs:space-y-2 mobile-sm:space-y-3">
          {data.map((item, index) => (
            <TableCard
              key={index}
              title={cardTitle?.(item)}
              subtitle={cardSubtitle?.(item)}
              actions={actions?.(item)}
            >
              {columns
                .filter(col => !(col.hideOnMobile && col.priority === "low"))
                .map((column) => (
                  <TableCardField
                    key={column.key}
                    label={column.header}
                    value={column.render ? column.render(item) : item[column.key]}
                    priority={column.priority}
                    hideOnMobile={column.hideOnMobile}
                  />
                ))}
            </TableCard>
          ))}
        </div>
      </div>

      {/* Desktop Table Layout */}
      <div className={tableClasses[breakpoint]}>
        <Table responsive="scroll" touchOptimized>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead 
                  key={column.key}
                  priority={column.priority}
                  hideOnMobile={column.hideOnMobile}
                >
                  {column.header}
                </TableHead>
              ))}
              {actions && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index}>
                {columns.map((column) => (
                  <TableCell 
                    key={column.key}
                    priority={column.priority}
                    hideOnMobile={column.hideOnMobile}
                  >
                    {column.render ? column.render(item) : item[column.key]}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {actions(item)}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableCard,
  TableCardField,
  TableActionButton,
  ResponsiveTable,
}
